/**
 * CSS Custom Properties Generation Utility
 * Converts design tokens to CSS custom properties with proper naming conventions
 */

import { DesignTokens, CSSCustomProperties } from '../tokens';

// Naming convention configuration
interface NamingConfig {
  prefix: string;
  separator: string;
  caseStyle: 'kebab' | 'camel' | 'snake';
}

const defaultNamingConfig: NamingConfig = {
  prefix: '--',
  separator: '-',
  caseStyle: 'kebab',
};

// Convert camelCase to kebab-case
const toKebabCase = (str: string): string => {
  return str
    .replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2')
    .toLowerCase();
};

// Convert object path to CSS custom property name
const generatePropertyName = (
  path: string[],
  config: NamingConfig = defaultNamingConfig
): string => {
  const processedPath = path.map(segment => {
    if (config.caseStyle === 'kebab') {
      return toKebabCase(segment);
    }
    return segment;
  });

  return config.prefix + processedPath.join(config.separator);
};

// Recursively flatten nested token object
const flattenTokens = (
  obj: any,
  path: string[] = [],
  result: CSSCustomProperties = {}
): CSSCustomProperties => {
  for (const [key, value] of Object.entries(obj)) {
    const currentPath = [...path, key];

    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      // Recursively flatten nested objects
      flattenTokens(value, currentPath, result);
    } else {
      // Generate CSS custom property
      const propertyName = generatePropertyName(currentPath);
      
      // Convert value to string if needed
      let cssValue: string;
      if (Array.isArray(value)) {
        // Handle font family arrays
        cssValue = value.join(', ');
      } else if (typeof value === 'number') {
        cssValue = value.toString();
      } else {
        cssValue = String(value);
      }

      result[propertyName] = cssValue;
    }
  }

  return result;
};

// Main function to generate CSS custom properties from design tokens
export const generateCSSCustomProperties = (
  tokens: DesignTokens,
  config?: Partial<NamingConfig>
): CSSCustomProperties => {
  const fullConfig = { ...defaultNamingConfig, ...config };
  const result: CSSCustomProperties = {};

  // Process each token category
  flattenTokens(tokens.primitive, ['primitive'], result);
  flattenTokens(tokens.semantic, ['semantic'], result);

  return result;
};

// Generate CSS string from custom properties
export const generateCSSString = (
  customProperties: CSSCustomProperties,
  selector: string = ':root'
): string => {
  const declarations = Object.entries(customProperties)
    .map(([property, value]) => `  ${property}: ${value};`)
    .join('\n');

  return `${selector} {\n${declarations}\n}`;
};
